import { _basePortalApi, Tag } from "common/state/portalApi/base";
import { AlarmResponse } from "protos/portal/alarms";
import {
  AlmanacConfig,
  DiscriminatorConfig,
  ModelinatorConfig,
} from "protos/almanac/almanac";
import {
  AssignedModulesResponse,
  ReaperConfiguration,
  ReaperModuleHistoryResponse,
} from "protos/portal/reaper";
import { BatchPortalJobsResponse, PortalJob } from "protos/portal/jobs";
import { BlocksByJobResponse, BlocksResponse } from "protos/portal/spatial";
import { BulkEditRequest, BulkEditResponse } from "protos/robot_syncer/bulk";
import {
  CategoryChange,
  ListPlantProfilesResponse,
  SavedCategory,
  SavedPlantProfile,
} from "protos/robot_syncer/plant_profile";
import {
  ConfigNode,
  ConfigType,
  SchemaNode,
} from "protos/config/api/config_service";
import { ConfigResponse } from "protos/portal/configs";
import {
  CreateCategoryCollectionSessionRequest,
  CreateCategoryCollectionSessionResponse,
  Crop,
  Image,
  Model,
} from "protos/portal/veselka";
import { CropMapById } from "portal/utils/crops";
import { CustomerResponse } from "protos/portal/customers";
import {
  DailyMetricResponse,
  DailyMetricsByDateByRobotResponse,
  JobMetricsByJobsByRobotResponse,
} from "protos/portal/metrics";
import { findWhere, sortBy, spliceIfExists } from "portal/utils/arrays";
import { GetConfigAuditLogsResponse } from "protos/robot_syncer/audit";
import { getModelinatorId } from "portal/utils/almanac";
import {
  getNodeFromPath,
  getParentNodeFromPath,
  setValue,
  sortByName,
  sortSchemaByName,
} from "portal/utils/configs";
import {
  GetPredictionPointSessionResponse,
  ListPredictionPointsResponse,
} from "protos/veselka/prediction_point";
import { GlobalAlarmlists, RobotAlarmlists } from "protos/portal/admin";
import { Globals, UpdateGlobalsRequest } from "protos/portal/global";
import { HardwareResponse } from "protos/portal/hardware";
import { HistoryResponse } from "protos/portal/health";
import { isStalePlantProfileSaveError } from "portal/utils/errors";
import {
  JigsawBlocksByJobResponse,
  JigsawBlocksResponse,
  JigsawMetricsView,
} from "protos/portal/jigsaw";
import {
  ListCategoriesResponse,
  ListCategoryCollectionHistoryResponse,
  ListCategoryHistoryResponse,
  PlantProfileChanges,
  SavedPlantProfile as PortalSavedPlantProfile,
  ProfileListRequest,
  SavedLibraryCategory,
} from "protos/portal/category_profile";
import { Method, Pagination } from "common/utils/api";
import {
  PredictionPointApiBaseFilters,
  PredictionPointFilters,
} from "portal/utils/categoryCollection/predictionPoints";
import {
  ReportInstanceResponse,
  ReportResponse,
  ReportsRequest,
  ReportsResponse,
} from "protos/portal/reports";
import { RobotAlmanacs } from "protos/portal/almanac";
import { RobotClass, robotSort } from "portal/utils/robots";
import {
  RobotOTPResponse,
  RobotResponse,
  RobotSummaryResponse,
} from "protos/portal/robots";
import { TagDescription } from "@reduxjs/toolkit/query/react";
import { TVEProfile } from "protos/target_velocity_estimator/target_velocity_estimator";

export const portalApi = _basePortalApi.injectEndpoints({
  endpoints: (builder) => ({
    // AWS
    getS3UploadUrl: builder.query<string, { filename: string }>({
      query: ({ filename }) => ({
        url: `aws/s3/upload`,
        params: { filename },
      }),
      transformResponse: ({ url }: { url: string }) => url,
    }),

    // ALARMS
    listAlarms: builder.query<AlarmResponse[], void>({
      query: () => ({
        url: `alarms`,
      }),
      transformResponse: (response: AlarmResponse[]) =>
        response.map((alarm) => AlarmResponse.fromJSON(alarm)),
      providesTags: (result) =>
        result
          ? result.map((alarm) => ({ type: Tag.ALARM, id: alarm.db?.id }))
          : [],
    }),

    // HARDWARE
    getHardware: builder.query<HardwareResponse, void>({
      query: () => `hardware`,
      transformResponse: (response: HardwareResponse) =>
        HardwareResponse.fromJSON(response),
      providesTags: (result) => {
        const tags: TagDescription<any>[] = [];
        const robots = new Map<number, boolean>();
        if (result?.lasers) {
          for (const laser of result.lasers) {
            robots.set(laser.robotId, true);
          }
        }
        for (const robotId of robots.keys()) {
          tags.push({
            type: Tag.ROBOT_HARDWARE,
            id: robotId,
          });
        }
        return tags;
      },
    }),

    // CONFIGS
    getConfig: builder.query<
      ConfigResponse,
      { serial: string; fetchFirst?: boolean; noCache?: boolean }
    >({
      keepUnusedDataFor: 60 * 1, // 1 minute
      query: ({ serial, ...options }) => {
        if (options.noCache) {
          portalApi.util.invalidateTags([{ type: Tag.CONFIG, id: serial }]);
        }
        return { url: `configs/${serial}`, params: options };
      },
      transformResponse: (response: ConfigResponse) => {
        const { config, template, ...result } =
          ConfigResponse.fromJSON(response);
        return {
          ...result,
          config: config ? sortByName(config) : undefined,
          template: template ? sortByName(template) : undefined,
        };
      },
      providesTags: (result, error, { serial }) => [
        { type: Tag.CONFIG, id: serial },
      ],
    }),
    setConfigValue: builder.mutation<
      undefined,
      {
        serial: string;
        path: string;
        value: any;
        reason: string;
        configType: ConfigType | undefined;
      }
    >({
      query: ({ serial, path, value, reason, configType }) => ({
        url: `configs/${serial}`,
        method: Method.POST,
        body: { path, value, reason, configType },
      }),
      invalidatesTags: (_resp, _bq, { serial, path }) => [
        {
          type: Tag.CONFIG,
          id: serial,
        },
        { type: Tag.CONFIG_AUDIT_LOGS, serial, key: path },
      ],
    }),
    deleteConfigPath: builder.mutation<
      undefined,
      { serial: string; path: string; reason: string }
    >({
      query: ({ serial, path, reason }) => ({
        url: `configs/${serial}/${path}`,
        method: Method.DELETE,
        body: { reason },
      }),
      invalidatesTags: (_resp, _bq, { serial, path }) => [
        {
          type: Tag.CONFIG,
          id: serial,
        },
        { type: Tag.CONFIG_AUDIT_LOGS, serial, key: path },
      ],
    }),
    bulkEditConfig: builder.mutation<BulkEditResponse, BulkEditRequest>({
      query: (request) => ({
        url: "configs/bulk-edit",
        method: Method.POST,
        body: BulkEditRequest.toJSON(request),
      }),
      transformResponse: (response: unknown) =>
        BulkEditResponse.fromJSON(response),
      invalidatesTags: (response: BulkEditResponse | undefined) => {
        return (response?.robots ?? []).map(({ serial }) => ({
          type: Tag.CONFIG,
          id: serial,
        }));
      },
    }),

    // CONFIG TEMPLATES
    getConfigTemplate: builder.query<ConfigNode, { serial: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ serial }) => ({ url: `configs/templates/${serial}` }),
      transformResponse: (response: ConfigNode) => {
        const result = ConfigNode.fromJSON(response);
        return sortByName(result);
      },
      providesTags: (result, error, { serial }) => [
        { type: Tag.CONFIG_TEMPLATE, id: serial },
      ],
    }),
    setConfigTemplateValue: builder.mutation<
      undefined,
      {
        serial: string;
        path: string;
        value: any;
        reason: string;
        configType: ConfigType | undefined;
      }
    >({
      query: ({ serial, path, value, reason, configType }) => ({
        url: `configs/templates/${serial}`,
        method: Method.POST,
        body: { path, value, timestamp: Date.now(), reason, configType },
      }),
      onQueryStarted: async (
        { serial, path, value },
        { dispatch, queryFulfilled }
      ) => {
        await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "getConfigTemplate",
            { serial },
            (template) => {
              const node = getNodeFromPath(template, path);
              if (!node) {
                dispatch(
                  portalApi.util.invalidateTags([
                    { type: Tag.CONFIG_TEMPLATE, id: serial },
                  ])
                );
                return template;
              }
              setValue(node, value);
              return template;
            }
          )
        );
      },
    }),
    deleteConfigTemplatePath: builder.mutation<
      undefined,
      { serial: string; path: string; reason: string }
    >({
      query: ({ serial, path }) => ({
        url: `configs/templates/${serial}`,
        method: Method.DELETE,
        body: {
          path,
          timestamp: Date.now(),
        },
      }),
      onQueryStarted: async (
        { serial, path },
        { dispatch, queryFulfilled }
      ) => {
        await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "getConfigTemplate",
            { serial },
            (template) => {
              const node = getParentNodeFromPath(template, path);
              const keyName = path.split("/").pop();
              if (!node?.children) {
                dispatch(
                  portalApi.util.invalidateTags([
                    { type: Tag.CONFIG_TEMPLATE, id: serial },
                  ])
                );
                return template;
              }
              for (let index = node.children.length - 1; index > 0; index--) {
                const child = node.children[index];
                if (child?.name === keyName) {
                  node.children.splice(index, 1);
                }
              }
              return template;
            }
          )
        );
      },
    }),

    // CONFIG SCHEMAS
    getConfigSchema: builder.query<SchemaNode, { robotClass: RobotClass }>({
      query: ({ robotClass }) => ({ url: `configs/schemas/${robotClass}` }),
      transformResponse: (response: SchemaNode) => {
        const result = SchemaNode.fromJSON(response);
        return sortSchemaByName(result);
      },
      providesTags: (result, error, { robotClass }) => [
        { type: Tag.CONFIG_SCHEMA, id: robotClass },
      ],
    }),

    // CONFIG AUDIT LOGS
    getConfigAuditLog: builder.query<
      GetConfigAuditLogsResponse,
      { serial: string; key: string }
    >({
      query: ({ serial, key }) => ({
        method: Method.GET,
        url: `rosy_audit_logs/${serial}`,
        params: { key },
      }),
      transformResponse: (response: unknown) =>
        GetConfigAuditLogsResponse.fromJSON(response),
      providesTags: (_r, _e, { serial, key }) => [
        { type: Tag.CONFIG_AUDIT_LOGS, serial, key },
      ],
    }),

    // CUSTOMERS
    listCustomers: builder.query<CustomerResponse[], void>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: () => "customers",
      transformResponse: (response: CustomerResponse[]) =>
        sortBy(response, "name").map((customer) =>
          CustomerResponse.fromJSON(customer)
        ),
      providesTags: (customers) => [
        Tag.CUSTOMER,
        ...(customers?.map((customer) => ({
          type: Tag.CUSTOMER,
          id: customer.db?.id,
        })) ?? []),
      ],
    }),
    getCustomer: builder.query<CustomerResponse, { customerUuid: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ customerUuid }) => `customers/${customerUuid}`,
      transformResponse: (customer) => CustomerResponse.fromJSON(customer),
      onQueryStarted: async (
        { customerUuid },
        { dispatch, queryFulfilled }
      ) => {
        const { data: customer } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listCustomers",
            undefined,
            (customers) => {
              const index = customers.findIndex(
                (listed) => listed.uuid === customerUuid
              );
              if (index >= 0) {
                customers[index] = customer;
                return customers;
              }
              return sortBy([...customers, customer], "name");
            }
          )
        );
      },
      providesTags: (customer) => [
        { type: Tag.CUSTOMER, id: customer?.db?.id },
      ],
    }),
    createCustomer: builder.mutation<
      CustomerResponse,
      Omit<CustomerResponse, "db" | "featureFlags" | "uuid">
    >({
      query: (customer) => ({
        url: "customers",
        method: Method.POST,
        body: CustomerResponse.toJSON(CustomerResponse.fromPartial(customer)),
      }),
      transformResponse: (customer) => CustomerResponse.fromJSON(customer),
      onQueryStarted: async (customer, { dispatch, queryFulfilled }) => {
        const { data: newCustomer } = await queryFulfilled;
        if (!newCustomer.db?.id) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "listCustomers",
            undefined,
            (customers) => {
              customers.push(newCustomer);
              return customers;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getCustomer",
            { customerUuid: newCustomer.uuid },
            () => newCustomer
          )
        );
      },
    }),
    updateCustomer: builder.mutation<
      CustomerResponse,
      {
        customerId: number;
        customer: Omit<CustomerResponse, "db" | "featureFlags">;
      }
    >({
      query: ({ customerId, customer }) => ({
        url: `customers/${customerId}`,
        method: Method.POST,
        body: CustomerResponse.toJSON(CustomerResponse.fromPartial(customer)),
      }),
      transformResponse: (customer) => CustomerResponse.fromJSON(customer),
      onQueryStarted: async ({ customerId }, { dispatch, queryFulfilled }) => {
        const { data: updatedCustomer } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listCustomers",
            undefined,
            (customers) => {
              const index = customers.findIndex(
                (customer) => customer.db?.id === customerId
              );
              customers[index] = updatedCustomer;
              return customers;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getCustomer",
            { customerUuid: updatedCustomer.uuid },
            () => updatedCustomer
          )
        );
      },
    }),

    // REPORTS
    listReports: builder.query<ReportsResponse, ReportsRequest>({
      query: (params) => ({
        url: "reports",
        params,
      }),
      transformResponse: (response: ReportsResponse) =>
        ReportsResponse.fromJSON(response),
      providesTags: (reports) => [
        Tag.REPORT,
        ...(reports?.reports.map((report) => ({
          type: Tag.REPORT,
          id: report.slug,
        })) ?? []),
      ],
    }),
    getReport: builder.query<
      ReportResponse,
      { reportSlug: string; instance?: string }
    >({
      query: ({ reportSlug, instance }) => ({
        url: `reports/${reportSlug}`,
        params: { instance },
      }),
      transformResponse: (response: ReportResponse) =>
        ReportResponse.fromJSON(response),
      providesTags: (report) => [{ type: Tag.REPORT, id: report?.slug }],
    }),
    createReport: builder.mutation<ReportResponse, ReportResponse>({
      query: (report) => ({
        url: "reports/",
        method: Method.POST,
        body: ReportResponse.toJSON(ReportResponse.fromPartial(report)),
      }),
      transformResponse: (response: ReportResponse) =>
        ReportResponse.fromJSON(response),
      onQueryStarted: async (report, { dispatch, queryFulfilled }) => {
        const { data: newReport } = await queryFulfilled;
        if (!newReport.db?.id) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "getReport",
            { reportSlug: newReport.slug },
            () => newReport
          )
        );
      },
    }),
    updateReport: builder.mutation<ReportResponse, ReportResponse>({
      query: (report) => ({
        url: `reports/${report.slug}`,
        method: Method.POST,
        body: ReportResponse.toJSON(ReportResponse.fromPartial(report)),
      }),
      transformResponse: (response: ReportResponse) =>
        ReportResponse.fromJSON(response),
      onQueryStarted: async (report, { dispatch, queryFulfilled }) => {
        const { data: updatedReport } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "getReport",
            { reportSlug: updatedReport.slug },
            () => updatedReport
          )
        );
      },
    }),
    deleteReport: builder.mutation<void, { slug: string }>({
      query: ({ slug }) => ({
        url: `reports/${slug}`,
        method: Method.DELETE,
      }),
      onQueryStarted: async ({ slug }, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          portalApi.util.invalidateTags([{ type: Tag.REPORT, id: slug }])
        );
      },
    }),
    listReportInstances: builder.query<
      ReportInstanceResponse[],
      { slug: string }
    >({
      query: ({ slug }) => `reports/${slug}/runs`,
      transformResponse: (response: ReportInstanceResponse[]) =>
        response.map((instance) => ReportInstanceResponse.fromJSON(instance)),
      providesTags: (instances) => [
        Tag.REPORT_INSTANCE,
        ...(instances?.map((instance) => ({
          type: Tag.REPORT_INSTANCE,
          id: instance.slug,
        })) ?? []),
      ],
    }),
    getReportInstance: builder.query<
      ReportInstanceResponse,
      { reportSlug: string; instanceSlug: string }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ reportSlug, instanceSlug }) =>
        `reports/${reportSlug}/runs/${instanceSlug}`,
      transformResponse: (response: ReportInstanceResponse) =>
        ReportInstanceResponse.fromJSON(response),
      providesTags: (instance) => [
        { type: Tag.REPORT_INSTANCE, id: instance?.slug },
      ],
      onQueryStarted: async (
        { reportSlug, instanceSlug },
        { dispatch, queryFulfilled }
      ) => {
        const { data: instance } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listReportInstances",
            { slug: reportSlug },
            (instances) => {
              const index = instances.findIndex(
                (instance) => instance.slug === instanceSlug
              );
              instances[index] = instance;
              return instances;
            }
          )
        );
      },
    }),
    createReportInstance: builder.mutation<
      ReportInstanceResponse,
      { reportSlug: string; instance: ReportInstanceResponse }
    >({
      query: ({ reportSlug, instance }) => ({
        url: `reports/${reportSlug}/runs`,
        method: Method.POST,
        body: ReportInstanceResponse.toJSON(
          ReportInstanceResponse.fromPartial(instance)
        ),
      }),
      transformResponse: (response: ReportInstanceResponse) =>
        ReportInstanceResponse.fromJSON(response),
      onQueryStarted: async ({ reportSlug }, { dispatch, queryFulfilled }) => {
        const { data: newInstance } = await queryFulfilled;
        if (!newInstance.db?.id) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "listReportInstances",
            { slug: reportSlug },
            (instances) => {
              instances.push(newInstance);
              return instances;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getReportInstance",
            { reportSlug, instanceSlug: newInstance.slug },
            () => newInstance
          )
        );
      },
    }),
    updateReportInstance: builder.mutation<
      ReportInstanceResponse,
      { reportSlug: string; instance: ReportInstanceResponse }
    >({
      query: ({ reportSlug, instance }) => ({
        url: `reports/${reportSlug}/runs/${instance.slug}`,
        method: Method.POST,
        body: ReportInstanceResponse.toJSON(
          ReportInstanceResponse.fromPartial(instance)
        ),
      }),
      transformResponse: (response: ReportInstanceResponse) =>
        ReportInstanceResponse.fromJSON(response),
      onQueryStarted: async ({ reportSlug }, { dispatch, queryFulfilled }) => {
        const { data: updatedInstance } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listReportInstances",
            { slug: reportSlug },
            (instances) => {
              const index = instances.findIndex(
                (instance) => instance.slug === updatedInstance.slug
              );
              instances[index] = updatedInstance;
              return instances;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getReportInstance",
            { reportSlug, instanceSlug: updatedInstance.slug },
            () => updatedInstance
          )
        );
      },
    }),
    deleteReportInstance: builder.mutation<
      void,
      { reportSlug: string; instanceSlug: string }
    >({
      query: ({ reportSlug, instanceSlug }) => ({
        url: `reports/${reportSlug}/runs/${instanceSlug}`,
        method: Method.DELETE,
      }),
      onQueryStarted: async (
        { reportSlug, instanceSlug },
        { dispatch, queryFulfilled }
      ) => {
        await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listReportInstances",
            { slug: reportSlug },
            (instances) => {
              spliceIfExists(
                instances,
                (instance) => instance.slug === instanceSlug,
                1
              );
              return instances;
            }
          )
        );
        dispatch(
          portalApi.util.invalidateTags([
            { type: Tag.REPORT_INSTANCE, id: instanceSlug },
          ])
        );
      },
    }),
    getDailyCertifiedMetrics: builder.mutation<
      DailyMetricsByDateByRobotResponse,
      {
        serials: string[];
        startDate: string;
        endDate: string;
        fields: string[];
        language: string;
        useJigsaw: boolean;
      }
    >({
      query: (body) => ({
        url: "metrics/certified/daily",
        method: Method.POST,
        body,
      }),
      transformResponse: (response: DailyMetricsByDateByRobotResponse) =>
        DailyMetricsByDateByRobotResponse.fromJSON(response),
    }),
    getJobCertifiedMetrics: builder.mutation<
      JobMetricsByJobsByRobotResponse,
      {
        serials: string[];
        startDate: string;
        endDate: string;
        fields: string[];
        language: string;
        useJigsaw: boolean;
      }
    >({
      query: (body) => ({
        url: "metrics/certified/jobs",
        method: Method.POST,
        body,
      }),
      transformResponse: (response: JobMetricsByJobsByRobotResponse) =>
        JobMetricsByJobsByRobotResponse.fromJSON(response),
    }),

    // ROBOTS
    listRobots: builder.query<
      RobotSummaryResponse[],
      {
        classes?: RobotClass[];
        excludeCustomer?: boolean;
        instance?: string;
        withAlarms?: boolean;
        withLasers?: boolean;
      } & (
        | {
            excludeMetrics?: true;
            language?: never;
            latestMetrics?: never;
            useJigsaw?: never;
          }
        | {
            excludeMetrics: false;
            language: string;
            latestMetrics?: boolean;
            useJigsaw: boolean;
          }
      )
    >({
      keepUnusedDataFor: 60 * 1, // 1 minute
      query: (args) => {
        const { classes, excludeCustomer, instance, withAlarms, withLasers } =
          args;
        const baseParams = {
          classes: classes?.join(","),
          excludeCustomer,
          showOffline: true,
          showInternal: true,
          instance,
          withAlarms,
          withLasers,
        };
        return {
          url: `robots`,
          params:
            args.excludeMetrics === true || args.excludeMetrics === undefined
              ? { ...baseParams, excludeMetrics: true }
              : {
                  ...baseParams,
                  excludeMetrics: false,
                  language: args.language,
                  latestMetrics: args.latestMetrics ?? true,
                  useJigsaw: args.useJigsaw,
                },
        };
      },
      transformResponse: (response: RobotSummaryResponse[] = []) => {
        response.sort(robotSort);
        for (const summary of response) {
          if (summary.config) {
            summary.config = sortByName(ConfigNode.fromJSON(summary.config));
          }
        }
        return response.map((summary) =>
          RobotSummaryResponse.fromJSON(summary)
        );
      },
      providesTags: (summaries) => [
        Tag.ROBOT,
        ...(summaries?.map((summary) => ({
          type: Tag.ROBOT,
          id: summary.robot?.db?.id,
        })) ?? []),
      ],
    }),
    getRobot: builder.query<
      RobotSummaryResponse,
      {
        serial: string;
        // when any include* flag is set, only those sections are loaded
        includeAlarms?: boolean;
        includeConfig?: boolean;
        includeCustomer?: boolean;
        includeHealthLogs?: boolean;
        includeLasers?: boolean;
      } & (
        | {
            // full fetch (default) or explicit metrics — useJigsaw required.
            includeDailyMetrics: true;
            language: string;
            useJigsaw: boolean;
          }
        | {
            // if nothing is included, everything gets included *sigh*
            includeAlarms?: false;
            includeConfig?: false;
            includeCustomer?: false;
            includeDailyMetrics?: undefined;
            includeHeathLogs?: false;
            language: string;
            useJigsaw: boolean;
          }
        | {
            // Opt out of metrics
            includeDailyMetrics: false;
            language?: never;
            useJigsaw?: never;
          }
      )
    >({
      keepUnusedDataFor: 60 * 1, // 1 minute
      query: ({ serial, ...params }) => ({
        url: `robots/${serial}`,
        params,
      }),
      providesTags: (summary) => [
        { type: Tag.ROBOT, id: summary?.robot?.db?.id },
        { type: Tag.CONFIG, id: summary?.robot?.serial },
        ...(summary?.alarmList
          ? summary.alarmList.map((alarm) => ({
              type: Tag.ALARM,
              id: alarm.db?.id,
            }))
          : []),
      ],
      transformResponse: (summary: RobotSummaryResponse) =>
        RobotSummaryResponse.fromJSON(summary),
      onQueryStarted: async (
        {
          serial,
          useJigsaw,
          includeAlarms,
          includeConfig,
          includeCustomer,
          includeDailyMetrics,
          includeHealthLogs,
          includeLasers,
        },
        { dispatch, getState, queryFulfilled }
      ) => {
        // Partial include* responses must not overwrite full list entries.
        const isPartial = Boolean(
          includeAlarms ||
            includeConfig ||
            includeCustomer ||
            includeDailyMetrics ||
            includeHealthLogs ||
            includeLasers
        );
        if (isPartial) {
          return;
        }
        const { data: summary } = await queryFulfilled;
        for (const args of portalApi.util.selectCachedArgsForQuery(
          getState(),
          "listRobots"
        )) {
          // only patch lists fetched with the same metric source
          const argsUseJigsaw = "useJigsaw" in args ? args.useJigsaw : false;
          if (Boolean(argsUseJigsaw) !== Boolean(useJigsaw)) {
            continue;
          }
          dispatch(
            portalApi.util.updateQueryData("listRobots", args, (robots) => {
              const index = robots.findIndex(
                (robot) => robot.robot?.serial === serial
              );
              if (index !== -1) {
                robots[index] = summary;
              }
              return robots;
            })
          );
        }
      },
    }),
    getRobotOtp: builder.query<RobotOTPResponse, { serial: string }>({
      keepUnusedDataFor: 0, // no caching
      query: ({ serial }) => `robots/${serial}/otp-code`,
      transformResponse: (response: RobotOTPResponse) =>
        RobotOTPResponse.fromJSON(response),
    }),
    getRobotHistory: builder.query<
      HistoryResponse,
      { serial: string; date: string }
    >({
      query: ({ serial, date }) => `robots/${serial}/history/${date}`,
      transformResponse: (response: HistoryResponse) =>
        HistoryResponse.fromJSON(response),
    }),
    getRobotBlocks: builder.query<
      BlocksResponse,
      { serial: string; date: string }
    >({
      query: ({ serial, date }) => `robots/${serial}/blocks/${date}`,
      transformResponse: (response: BlocksResponse) =>
        BlocksResponse.fromJSON(response),
    }),
    getRobotJigsawBlocks: builder.query<
      JigsawBlocksResponse,
      { language: string; serial: string; date: string }
    >({
      query: ({ language, serial, date }) => ({
        url: `robots/${serial}/jigsawBlocks/${date}`,
        params: {
          metricsView: JigsawMetricsView.JIGSAW_METRICS_VIEW_OPS_CENTER,
          includeHardwareState: true,
          language,
        },
      }),
      transformResponse: (response: JigsawBlocksResponse) =>
        JigsawBlocksResponse.fromJSON(response),
    }),
    getRobotMetrics: builder.query<
      DailyMetricResponse,
      { serial: string; date: string; language: string; useJigsaw: boolean }
    >({
      query: ({ serial, date, language, useJigsaw }) => ({
        params: {
          language,
          useJigsaw,
        },
        url: `robots/${serial}/metrics/${date}`,
      }),
      transformResponse: (response: DailyMetricResponse) =>
        DailyMetricResponse.fromJSON(response),
      providesTags: (result, error, { serial, date }) => [
        { type: Tag.METRIC, id: `${serial}-${date}` },
      ],
    }),
    createRobot: builder.mutation<
      RobotResponse,
      { serial: string; copyFrom: string }
    >({
      query: (parameters) => ({
        url: "robots",
        method: Method.POST,
        body: parameters,
      }),
      transformResponse: (summary: RobotResponse) =>
        RobotResponse.fromJSON(summary),
      // can't splice this into the cache because it's not a RobotSummaryResponse
      invalidatesTags: () => [Tag.ROBOT],
    }),
    updateRobot: builder.mutation<
      RobotResponse,
      {
        serial: string;
        robot: Partial<
          Pick<RobotResponse, "implementationStatus" | "supportSlack">
        >;
      }
    >({
      query: ({ serial, robot }) => ({
        url: `robots/${serial}`,
        method: Method.POST,
        body: RobotResponse.toJSON(RobotResponse.fromPartial(robot)),
      }),
      transformResponse: (summary: RobotResponse) =>
        RobotResponse.fromJSON(summary),
      onQueryStarted: async (
        { serial },
        { dispatch, getState, queryFulfilled }
      ) => {
        const { data: robot } = await queryFulfilled;
        for (const args of portalApi.util.selectCachedArgsForQuery(
          getState(),
          "listRobots"
        )) {
          dispatch(
            portalApi.util.updateQueryData("listRobots", args, (summaries) => {
              const index = summaries.findIndex(
                (instance) => instance.robot?.serial === serial
              );
              if (summaries[index]) {
                summaries[index].robot = robot;
              }
              return summaries;
            })
          );
        }
        for (const args of portalApi.util.selectCachedArgsForQuery(
          getState(),
          "getRobot"
        )) {
          if (args.serial !== serial) {
            continue;
          }
          dispatch(
            portalApi.util.updateQueryData("getRobot", args, (summary) => {
              summary.robot = robot;
              return summary;
            })
          );
        }
      },
    }),
    assignRobot: builder.mutation<
      RobotResponse,
      { serial: string; customerId: number }
    >({
      query: ({ serial, customerId }) => ({
        url: `robots/${serial}/assign`,
        method: Method.POST,
        body: { customerId },
      }),
      transformResponse: (summary: RobotResponse) =>
        RobotResponse.fromJSON(summary),
      // can't splice this into the cache because it's not a RobotSummaryResponse
      invalidatesTags: (robot, _err, { customerId }) => [
        Tag.ROBOT,
        Tag.USER,
        { type: Tag.ROBOT, id: robot?.db?.id },
        { type: Tag.CUSTOMER, id: customerId.toString() },
      ],
    }),
    listRobotJobs: builder.query<
      PortalJob[],
      {
        language: string;
        serial: string;
        startDate: string;
        endDate: string;
        useJigsaw: boolean;
      }
    >({
      query: ({ serial, startDate, endDate, language, useJigsaw }) => ({
        url: `robots/${serial}/jobs/${startDate}/${endDate}`,
        params: {
          language,
          useJigsaw,
        },
      }),
      transformResponse: (response: PortalJob[]) =>
        response.map((job) => PortalJob.fromJSON(job)),
      providesTags: (jobs) => [
        Tag.ROBOT_JOB,
        ...(jobs?.map((job) => ({
          type: Tag.ROBOT_JOB,
          id: job.jobId,
        })) ?? []),
      ],
    }),
    batchListRobotJobs: builder.query<
      Record<string, PortalJob[]>,
      {
        serials: string[];
        startDate: string;
        endDate: string;
        useJigsaw: boolean;
      }
    >({
      query: ({ serials, startDate, endDate, useJigsaw }) => ({
        url: "robots/jobs",
        method: "POST",
        body: { serials, startDate, endDate, useJigsaw },
      }),
      transformResponse: (response: BatchPortalJobsResponse) => {
        const parsed = BatchPortalJobsResponse.fromJSON(response);
        const result: Record<string, PortalJob[]> = {};
        for (const [serial, portalJobs] of Object.entries(parsed.jobsByRobot)) {
          result[serial] = portalJobs.jobs;
        }
        return result;
      },
      providesTags: (result) => [
        Tag.ROBOT_JOB,
        ...(result
          ? Object.values(result).flatMap((jobs) =>
              jobs.map((job) => ({
                type: Tag.ROBOT_JOB as const,
                id: job.jobId,
              }))
            )
          : []),
      ],
    }),
    getJob: builder.query<
      PortalJob,
      { jobId: string; language: string; useJigsaw: boolean }
    >({
      query: ({ jobId, language, useJigsaw }) => ({
        url: `jobs/${jobId}`,
        params: {
          language,
          useJigsaw,
        },
      }),
      transformResponse: (response: PortalJob) => PortalJob.fromJSON(response),
      providesTags: (job) => [
        Tag.ROBOT_JOB,
        { type: Tag.ROBOT_JOB, id: job?.jobId },
        { type: Tag.JOB_METRIC, id: job?.jobId },
      ],
    }),
    getJobHistory: builder.query<HistoryResponse, { jobId: string }>({
      query: ({ jobId }) => `jobs/${jobId}/history`,
      transformResponse: (response: HistoryResponse) =>
        HistoryResponse.fromJSON(response),
    }),
    getJobBlocks: builder.query<BlocksResponse, { jobId: string }>({
      query: ({ jobId }) => `jobs/${jobId}/blocks`,
      transformResponse: (response: BlocksResponse) =>
        BlocksResponse.fromJSON(response),
    }),
    getJobJigsawBlocks: builder.query<
      JigsawBlocksResponse,
      { jobId: string; language: string }
    >({
      query: ({ jobId, language }) => ({
        url: `jobs/${jobId}/jigsawBlocks`,
        params: {
          metricsView: JigsawMetricsView.JIGSAW_METRICS_VIEW_OPS_CENTER,
          includeHardwareState: true,
          language,
        },
      }),
      transformResponse: (response: JigsawBlocksResponse) =>
        JigsawBlocksResponse.fromJSON(response),
    }),
    batchGetJobSpatialBlocks: builder.query<
      BlocksByJobResponse & { queryLimitReached: boolean },
      { jobIds: string[]; language: string }
    >({
      query: ({ jobIds, language }) => ({
        url: "jobs/blocks",
        method: "POST",
        body: {
          jobIds,
          useJigsaw: false,
          includeHardwareState: false,
          language,
        },
      }),
      transformResponse: (response: BlocksByJobResponse) => {
        const parsed = BlocksByJobResponse.fromJSON(response);
        const queryLimitReached = Object.values(parsed.blocks).some(
          (b) => b.queryLimitReached
        );
        return { ...parsed, queryLimitReached };
      },
    }),
    batchGetJobJigsawBlocks: builder.query<
      JigsawBlocksByJobResponse & { queryLimitReached: boolean },
      { jobIds: string[]; includeHardwareState?: boolean; language: string }
    >({
      query: ({ jobIds, includeHardwareState = true, language }) => ({
        url: "jobs/blocks",
        method: "POST",
        body: { jobIds, useJigsaw: true, includeHardwareState, language },
      }),
      transformResponse: (response: JigsawBlocksByJobResponse) => {
        const parsed = JigsawBlocksByJobResponse.fromJSON(response);
        const queryLimitReached = Object.values(parsed.blocks).some(
          (b) => b.queryLimitReached
        );
        return { ...parsed, queryLimitReached };
      },
    }),
    getRobotHardware: builder.query<HardwareResponse, { serial: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ serial }) => `robots/${serial}/hardware`,
      transformResponse: (response: HardwareResponse) =>
        HardwareResponse.fromJSON(response),
      providesTags: (robotCrops, error, { serial }) => [
        Tag.ROBOT_HARDWARE,
        {
          type: Tag.ROBOT_HARDWARE,
          id: serial,
        },
      ],
    }),

    getReaperConfiguration: builder.query<
      ReaperConfiguration,
      { serial: string }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ serial }) => `robots/${serial}/reaper-configuration`,
      transformResponse: (response: ReaperConfiguration) =>
        ReaperConfiguration.fromJSON(response),
      providesTags: (config, error, { serial }) => [
        Tag.REAPER_CONFIG,
        {
          type: Tag.REAPER_CONFIG,
          id: serial,
        },
      ],
    }),

    getReaperConfigurationHistory: builder.query<
      ReaperModuleHistoryResponse,
      { serial: string }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ serial }) => `robots/${serial}/reaper-configuration/history`,
      transformResponse: (response: ReaperModuleHistoryResponse) =>
        ReaperModuleHistoryResponse.fromJSON(response),
      providesTags: [Tag.REAPER_CONFIG],
    }),

    listReaperModules: builder.query<AssignedModulesResponse, void>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: (_) => `hardware/reaper-modules`,
      transformResponse: (response: AssignedModulesResponse) =>
        AssignedModulesResponse.fromJSON(response),
      providesTags: (_, _error) => [Tag.REAPER_MODULES],
    }),

    setActiveAtkRig: builder.mutation<
      RobotResponse,
      { serial: string; rigId: string }
    >({
      query: ({ serial, rigId }) => ({
        url: `robots/${serial}/active-atk-rig/${rigId}`,
        method: "POST",
      }),
      transformResponse: (response: RobotResponse) =>
        RobotResponse.fromJSON(response),
      invalidatesTags: (response, _error) =>
        response ? [{ type: Tag.ROBOT, id: response.db?.id }] : [],
    }),

    setDefaultAtkTractor: builder.mutation<
      RobotResponse,
      { serial: string; tractorId: string }
    >({
      query: ({ serial, tractorId }) => ({
        url: `robots/${serial}/default-tractor/${tractorId}`,
        method: "POST",
      }),
      transformResponse: (response: RobotResponse) =>
        RobotResponse.fromJSON(response),
      invalidatesTags: (response, _error) =>
        response ? [{ type: Tag.ROBOT, id: response.db?.id }] : [],
    }),

    clearDefaultAtkTractor: builder.mutation<RobotResponse, { serial: string }>(
      {
        query: ({ serial }) => ({
          url: `robots/${serial}/default-tractor`,
          method: "DELETE",
        }),
        transformResponse: (response: RobotResponse) =>
          RobotResponse.fromJSON(response),
        invalidatesTags: (response, _error) =>
          response ? [{ type: Tag.ROBOT, id: response.db?.id }] : [],
      }
    ),

    // VESELKA
    listUploads: builder.query<
      Image[],
      { serial: string; pagination?: Pagination }
    >({
      query: ({ serial, pagination }) => ({
        url: `veselka/uploads/${serial}`,
        params: pagination,
      }),
      transformResponse: (images: Image[]) =>
        images.map((image) => Image.fromJSON(image)),
      providesTags: (images) => [
        Tag.IMAGE,
        ...(images?.map((image) => ({
          type: Tag.IMAGE,
          id: image.id,
        })) ?? []),
      ],
    }),
    listCrops: builder.query<CropMapById, void>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: () => "veselka/models/crops",
      transformResponse: (crops: Crop[]) =>
        Object.fromEntries(
          crops.map((rawCrop) => {
            const crop = Crop.fromJSON(rawCrop);
            return [crop.id, crop];
          })
        ),
      providesTags: (crops) => [
        Tag.CROP,
        ...Object.keys(crops ?? {}).map((cropId) => ({
          type: Tag.CROP,
          id: cropId,
        })),
      ],
    }),
    getCrop: builder.query<Crop | undefined, { cropId: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: () => "veselka/models/crops",
      providesTags: (crop) =>
        crop
          ? [
              {
                type: Tag.CROP,
                id: crop.id,
              },
            ]
          : [],
      transformResponse: (crops: Crop[], error, { cropId }) => {
        const crop = findWhere(crops, { id: cropId });
        if (!crop) {
          return;
        }
        return Crop.fromJSON(crop);
      },
      onQueryStarted: async ({ cropId }, { dispatch, queryFulfilled }) => {
        const { data: crop } = await queryFulfilled;
        if (!crop) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData("listCrops", undefined, (crops) => {
            crops[cropId] = crop;
            return crops;
          })
        );
      },
    }),
    listPredictionPoints: builder.query<
      ListPredictionPointsResponse,
      PredictionPointFilters
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: (filters) => ({
        url: "veselka/prediction-points",
        params: filters,
      }),
      transformResponse: (response: ListPredictionPointsResponse) =>
        ListPredictionPointsResponse.fromJSON(response),
    }),
    createCategoryCollectionSession: builder.mutation<
      CreateCategoryCollectionSessionResponse,
      Omit<CreateCategoryCollectionSessionRequest, "filters"> & {
        filters?: PredictionPointApiBaseFilters;
      }
    >({
      query: (body) => ({
        url: "veselka/category-collections/session",
        method: Method.POST,
        body,
      }),
      transformResponse: (response: CreateCategoryCollectionSessionResponse) =>
        CreateCategoryCollectionSessionResponse.fromJSON(response),
    }),
    getCategoryCollectionSession: builder.query<
      GetPredictionPointSessionResponse,
      { sessionId: string; filters?: PredictionPointApiBaseFilters }
    >({
      query: ({ sessionId, filters }) => ({
        url: `veselka/category-collections/session/${sessionId}`,
        params: filters ?? undefined,
      }),
      transformResponse: (response: GetPredictionPointSessionResponse) =>
        GetPredictionPointSessionResponse.fromJSON(response),
    }),
    getModel: builder.query<Model, { modelId: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ modelId }) => `veselka/models/${modelId}`,
      transformResponse: (model: Model) => Model.fromJSON(model),
      providesTags: (result, error, { modelId }) => [
        { type: Tag.MODEL, id: modelId },
      ],
    }),

    // ADMIN
    deleteCaches: builder.mutation<void, void>({
      query: () => ({
        url: `/admin/caches`,
        method: Method.DELETE,
      }),
    }),
    getGlobalAlarmLists: builder.query<GlobalAlarmlists, void>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: () => "/admin/alarms/lists",
      transformResponse: (response: GlobalAlarmlists) =>
        GlobalAlarmlists.fromJSON(response),
      providesTags: () => [
        {
          type: Tag.ADMIN_ALARM,
          id: -1,
        },
      ],
    }),
    getRobotAlarmLists: builder.query<RobotAlarmlists, { serial: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ serial }) => `/admin/alarms/lists/${serial}`,
      transformResponse: (response: RobotAlarmlists) =>
        RobotAlarmlists.fromJSON(response),
      providesTags: (result, error, { serial }) => [
        {
          type: Tag.ADMIN_ALARM,
          id: serial,
        },
      ],
    }),
    setGlobalAlarmLists: builder.mutation<
      GlobalAlarmlists,
      Omit<GlobalAlarmlists, "db">
    >({
      query: (lists) => ({
        url: "admin/alarms/lists",
        method: Method.POST,
        body: GlobalAlarmlists.toJSON(lists),
      }),
      transformResponse: (response: GlobalAlarmlists) =>
        GlobalAlarmlists.fromJSON(response),
      onQueryStarted: async (lists, { dispatch, queryFulfilled }) => {
        const { data: newLists } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "getGlobalAlarmLists",
            undefined,
            () => newLists
          )
        );
      },
    }),
    setRobotAlarmLists: builder.mutation<
      RobotAlarmlists,
      { serial: string; lists: Omit<RobotAlarmlists, "db"> }
    >({
      query: ({ serial, lists }) => ({
        url: `admin/alarms/lists/${serial}`,
        method: Method.POST,
        body: RobotAlarmlists.toJSON(lists),
      }),
      transformResponse: (response: GlobalAlarmlists) =>
        RobotAlarmlists.fromJSON(response),
      onQueryStarted: async ({ serial }, { dispatch, queryFulfilled }) => {
        const { data: newLists } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "getRobotAlarmLists",
            { serial },
            () => newLists
          )
        );
      },
    }),

    // ALMANANC
    listAlmanacsForRobot: builder.query<AlmanacConfig[], { serial?: string }>({
      query: ({ serial }) => `/profiles/almanacs/robots/${serial}`,
      transformResponse: (response: AlmanacConfig[]) =>
        response.map((almanac) => AlmanacConfig.fromJSON(almanac)),
      providesTags: (almanacs) =>
        almanacs?.map((almanac) => ({
          type: Tag.ALMANAC,
          id: almanac.id,
        })) ?? [],
    }),
    getAlmanac: builder.query<
      AlmanacConfig,
      {
        uuid: string;
      }
    >({
      query: ({ uuid }) => `/profiles/almanacs/profiles/${uuid}`,
      transformResponse: (response: AlmanacConfig) =>
        AlmanacConfig.fromJSON(response),
      providesTags: (result, error, { uuid }) => [
        {
          type: Tag.ALMANAC,
          id: uuid,
        },
      ],
    }),
    deleteAlmanac: builder.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/profiles/almanacs/profiles/${uuid}`,
        method: Method.DELETE,
      }),
      invalidatesTags: (result, error, { uuid }) => [
        { type: Tag.ALMANAC, id: uuid },
      ],
    }),
    setAlmanac: builder.mutation<
      AlmanacConfig,
      { serial?: string; almanac: AlmanacConfig }
    >({
      query: ({ serial, almanac }) => ({
        url: `/profiles/almanacs/robots/${serial}`,
        method: Method.POST,
        body: AlmanacConfig.toJSON(almanac),
      }),
      transformResponse: (response: AlmanacConfig) =>
        AlmanacConfig.fromJSON(response),
      onQueryStarted: async ({ serial }, { dispatch, queryFulfilled }) => {
        const { data: newAlmanac } = await queryFulfilled;
        if (!serial || !newAlmanac.id) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "listAlmanacsForRobot",
            { serial },
            (almanacs) => {
              const idx = almanacs.findIndex(
                (almanac) => almanac.id === newAlmanac.id
              );
              if (idx > -1) {
                almanacs[idx] = newAlmanac;
              } else {
                almanacs.push(newAlmanac);
              }
              return almanacs;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getAlmanac",
            { uuid: newAlmanac.id },
            () => newAlmanac
          )
        );
      },
    }),
    // any string is valid for the input. Just matching the signature of
    // listAlmanacsForRobot so they can be used interchangeably
    listGlobalAlmanacs: builder.query<AlmanacConfig[], { serial?: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: () => `/profiles/almanacs/global`,
      transformResponse: (response: AlmanacConfig[]) =>
        response.map((almanac) => AlmanacConfig.fromJSON(almanac)),
      providesTags: (almanacs) =>
        almanacs?.map((almanac) => ({
          type: Tag.ADMIN_ALMANAC,
          id: almanac.id,
        })) ?? [],
    }),
    listCustomerAlmanacsByCustomer: builder.query<
      RobotAlmanacs[],
      { customerUuid?: string }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: (params) => ({
        url: `/profiles/almanacs/customer`,
        params,
      }),
      transformResponse: (response: RobotAlmanacs[]) =>
        response.map((group) => ({
          serial: group.serial,
          almanacs: group.almanacs.map((almanac) =>
            AlmanacConfig.fromJSON(almanac)
          ),
        })),
      providesTags: (result) =>
        result?.flatMap((group) =>
          group.almanacs.map((almanac) => ({
            type: Tag.ALMANAC,
            id: almanac.id,
          }))
        ) ?? [Tag.ALMANAC],
    }),
    getGlobalAlmanac: builder.query<AlmanacConfig, { uuid: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid }) => `/profiles/almanacs/global/${uuid}`,
      transformResponse: (response: AlmanacConfig) =>
        AlmanacConfig.fromJSON(response),
      providesTags: (result, error, { uuid }) => [
        {
          type: Tag.ADMIN_ALMANAC,
          id: uuid,
        },
      ],
      onQueryStarted: async ({ uuid }, { dispatch, queryFulfilled }) => {
        const { data: newAlmanac } = await queryFulfilled;
        if (!newAlmanac.id) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "listGlobalAlmanacs",
            {},
            (almanacs) => {
              const index = almanacs.findIndex(
                (almanac) => almanac.id === uuid
              );
              if (index === -1) {
                almanacs.push(newAlmanac);
              } else {
                almanacs[index] = newAlmanac;
              }
              return almanacs;
            }
          )
        );
      },
    }),
    deleteGlobalAlmanac: builder.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/profiles/almanacs/global/${uuid}`,
        method: Method.DELETE,
      }),
      onQueryStarted: async ({ uuid }, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listGlobalAlmanacs",
            {},
            (almanacs) => {
              spliceIfExists(almanacs, (almanac) => almanac.id === uuid, 1);
              return almanacs;
            }
          )
        );
        dispatch(
          portalApi.util.invalidateTags([{ type: Tag.ADMIN_ALMANAC, id: uuid }])
        );
      },
    }),
    setGlobalAlmanac: builder.mutation<
      AlmanacConfig,
      { serial?: string; almanac: AlmanacConfig }
    >({
      query: ({ almanac }) => {
        return {
          url: `/profiles/almanacs/global/${almanac.id}`,
          method: Method.POST,
          body: AlmanacConfig.toJSON(almanac),
        };
      },
      transformResponse: (response: AlmanacConfig) =>
        AlmanacConfig.fromJSON(response),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data: newAlmanac } = await queryFulfilled;
        if (!newAlmanac.id) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "listGlobalAlmanacs",
            {},
            (almanacs) => {
              const index = almanacs.findIndex(
                (almanac) => almanac.id === newAlmanac.id
              );
              if (index === -1) {
                almanacs.push(newAlmanac);
              } else {
                almanacs[index] = newAlmanac;
              }
              return almanacs;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getGlobalAlmanac",
            { uuid: newAlmanac.id },
            () => newAlmanac
          )
        );
      },
    }),

    // DISCRIMINATOR
    listDiscriminatorsForRobot: builder.query<
      DiscriminatorConfig[],
      { serial: string }
    >({
      query: ({ serial }) => `/profiles/discriminators/robots/${serial}`,
      transformResponse: (response: DiscriminatorConfig[]) =>
        response.map((discriminator) =>
          DiscriminatorConfig.fromJSON(discriminator)
        ),
      providesTags: (discriminators) =>
        discriminators?.map((discrimininator) => ({
          type: Tag.DISCRIMINATOR,
          id: discrimininator.id,
        })) ?? [],
    }),
    getDiscriminator: builder.query<DiscriminatorConfig, { uuid: string }>({
      query: ({ uuid }) => `/profiles/discriminators/profiles/${uuid}`,
      transformResponse: (response: DiscriminatorConfig) =>
        DiscriminatorConfig.fromJSON(response),
      providesTags: (result, error, { uuid }) => [
        {
          type: Tag.DISCRIMINATOR,
          id: uuid,
        },
      ],
    }),
    deleteDiscriminator: builder.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/profiles/discriminators/profiles/${uuid}`,
        method: Method.DELETE,
      }),
      invalidatesTags: (result, error, { uuid }) => [
        { type: Tag.DISCRIMINATOR, id: uuid },
      ],
    }),
    setDiscriminator: builder.mutation<
      DiscriminatorConfig,
      { serial: string; discriminator: DiscriminatorConfig }
    >({
      query: ({ serial, discriminator }) => ({
        url: `/profiles/discriminators/robots/${serial}`,
        method: Method.POST,
        body: DiscriminatorConfig.toJSON(discriminator),
      }),
      transformResponse: (response: DiscriminatorConfig) =>
        DiscriminatorConfig.fromJSON(response),
      onQueryStarted: async ({ serial }, { dispatch, queryFulfilled }) => {
        const { data: newDiscriminator } = await queryFulfilled;
        if (!newDiscriminator.id) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "listDiscriminatorsForRobot",
            { serial },
            (discriminators) => {
              const index = discriminators.findIndex(
                (discriminator) => discriminator.id === newDiscriminator.id
              );
              if (index === -1) {
                discriminators.push(newDiscriminator);
              } else {
                discriminators[index] = newDiscriminator;
              }
              return discriminators;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getDiscriminator",
            { uuid: newDiscriminator.id },
            () => newDiscriminator
          )
        );
      },
    }),

    // MODELINATOR
    listModelinatorsForRobotAndCrop: builder.query<
      ModelinatorConfig[],
      { serial: string; cropId: string }
    >({
      query: ({ serial, cropId }) =>
        `/profiles/modelinators/robots/${serial}/crops/${cropId}`,
      transformResponse: (response: ModelinatorConfig[]) =>
        response.map((modelinator) => ModelinatorConfig.fromJSON(modelinator)),
      providesTags: (modelinators, error, { serial }) =>
        modelinators?.map((modelinator) => ({
          type: Tag.MODELINDATOR,
          id: getModelinatorId(serial, modelinator),
        })) ?? [],
    }),
    getModelinator: builder.query<
      ModelinatorConfig | undefined,
      { serial: string; cropId: string; modelId: string }
    >({
      query: ({ serial, cropId }) =>
        `/profiles/modelinators/robots/${serial}/crops/${cropId}`,
      transformResponse: (
        modelinators: ModelinatorConfig[],
        error,
        { cropId, modelId }
      ) => {
        const modelinator = findWhere(modelinators, { cropId, modelId });
        if (!modelinator) {
          return;
        }
        return ModelinatorConfig.fromJSON(modelinator);
      },
      onQueryStarted: async (
        { serial, cropId, modelId },
        { dispatch, queryFulfilled }
      ) => {
        const { data: newModelinator } = await queryFulfilled;
        if (!newModelinator) {
          return;
        }
        dispatch(
          portalApi.util.updateQueryData(
            "listModelinatorsForRobotAndCrop",
            { serial, cropId },
            (modelinators) => {
              const index = modelinators.findIndex(
                (modelinator) => modelinator.modelId === modelId
              );
              if (index === -1) {
                modelinators.push(newModelinator);
              } else {
                modelinators[index] = newModelinator;
              }
              return modelinators;
            }
          )
        );
      },
    }),
    setModelinator: builder.mutation<
      ModelinatorConfig,
      { serial: string; modelinator: ModelinatorConfig }
    >({
      query: ({ serial, modelinator }) => ({
        url: `/profiles/modelinators/robots/${serial}`,
        method: Method.POST,
        body: ModelinatorConfig.toJSON(modelinator),
      }),
      transformResponse: (response: ModelinatorConfig) =>
        ModelinatorConfig.fromJSON(response),
      onQueryStarted: async ({ serial }, { dispatch, queryFulfilled }) => {
        const { data: newModelinator } = await queryFulfilled;
        const { cropId, modelId } = newModelinator;
        dispatch(
          portalApi.util.updateQueryData(
            "listModelinatorsForRobotAndCrop",
            { serial, cropId: newModelinator.cropId },
            (modelinators) => {
              const index = modelinators.findIndex(
                (modelinator) => modelinator.modelId === newModelinator.modelId
              );
              if (index === -1) {
                modelinators.push(newModelinator);
              } else {
                modelinators[index] = newModelinator;
              }
              return modelinators;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getModelinator",
            { serial, cropId, modelId },
            () => newModelinator
          )
        );
      },
    }),

    // TARGET VELOCITY ESTIMATOR
    // any string is valid for the input. Just matching the signature of
    // listTargetVelocityEstimators so they can be used interchangeably
    listGlobalTargetVelocityEstimators: builder.query<
      TVEProfile[],
      { serial?: string }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: () => `/profiles/velocity/global`,
      transformResponse: (response: TVEProfile[]) =>
        response.map((targetVelocityEstimator) =>
          TVEProfile.fromJSON(targetVelocityEstimator)
        ),
      providesTags: (targetVelocityEstimators) =>
        targetVelocityEstimators?.map((targetVelocityEstimator) => ({
          type: Tag.TARGET_VELOCITY_ESTIMATOR,
          id: targetVelocityEstimator.id,
        })) ?? [],
    }),
    getGlobalTargetVelocityEstimator: builder.query<
      TVEProfile,
      { uuid: string }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid }) => `/profiles/velocity/global/${uuid}`,
      transformResponse: (response: TVEProfile) =>
        TVEProfile.fromJSON(response),
      providesTags: (result, error, { uuid }) => [
        {
          type: Tag.ADMIN_TARGET_VELOCITY_ESTIMATOR,
          id: uuid,
        },
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data: newTargetVelocityEstimator } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listGlobalTargetVelocityEstimators",
            {},
            (targetVelocityEstimators) => {
              const index = targetVelocityEstimators.findIndex(
                (targetVelocityEstimator) =>
                  targetVelocityEstimator.id === newTargetVelocityEstimator.id
              );
              if (index === -1) {
                targetVelocityEstimators.push(newTargetVelocityEstimator);
              } else {
                targetVelocityEstimators[index] = newTargetVelocityEstimator;
              }
              return targetVelocityEstimators;
            }
          )
        );
      },
    }),
    deleteTargetVelocityEstimator: builder.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/profiles/velocity/profiles/${uuid}`,
        method: Method.DELETE,
      }),
      invalidatesTags: (result, error, { uuid }) => [
        { type: Tag.TARGET_VELOCITY_ESTIMATOR, id: uuid },
      ],
    }),
    deleteGlobalTargetVelocityEstimator: builder.mutation<
      void,
      { uuid: string }
    >({
      query: ({ uuid }) => ({
        url: `/profiles/velocity/global/${uuid}`,
        method: Method.DELETE,
      }),
      onQueryStarted: async ({ uuid }, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listGlobalTargetVelocityEstimators",
            {},
            (targetVelocityEstimators) => {
              spliceIfExists(
                targetVelocityEstimators,
                (targetVelocityEstimator) =>
                  targetVelocityEstimator.id === uuid,
                1
              );
              return targetVelocityEstimators;
            }
          )
        );
        dispatch(
          portalApi.util.invalidateTags([
            { type: Tag.ADMIN_TARGET_VELOCITY_ESTIMATOR, id: uuid },
          ])
        );
      },
    }),
    setGlobalTargetVelocityEstimator: builder.mutation<
      TVEProfile,
      { serial?: string; targetVelocityEstimator: TVEProfile }
    >({
      query: ({ targetVelocityEstimator }) => {
        return {
          url: `/profiles/velocity/global/${targetVelocityEstimator.id}`,
          method: Method.POST,
          body: TVEProfile.toJSON(targetVelocityEstimator),
        };
      },
      transformResponse: (response: TVEProfile) =>
        TVEProfile.fromJSON(response),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data: newTargetVelocityEstimator } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listGlobalTargetVelocityEstimators",
            {},
            (targetVelocityEstimators) => {
              const index = targetVelocityEstimators.findIndex(
                (targetVelocityEstimator) =>
                  targetVelocityEstimator.id === newTargetVelocityEstimator.id
              );
              if (index === -1) {
                targetVelocityEstimators.push(newTargetVelocityEstimator);
              } else {
                targetVelocityEstimators[index] = newTargetVelocityEstimator;
              }
              return targetVelocityEstimators;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getGlobalTargetVelocityEstimator",
            { uuid: newTargetVelocityEstimator.id },
            () => newTargetVelocityEstimator
          )
        );
      },
    }),
    listTargetVelocityEstimators: builder.query<
      TVEProfile[],
      { serial?: string }
    >({
      query: ({ serial }) => `/profiles/velocity/robots/${serial}`,
      transformResponse: (response: TVEProfile[]) =>
        response.map((targetVelocityEstimator) =>
          TVEProfile.fromJSON(targetVelocityEstimator)
        ),
      providesTags: (targetVelocityEstimators) =>
        targetVelocityEstimators?.map((targetVelocityEstimator) => ({
          type: Tag.TARGET_VELOCITY_ESTIMATOR,
          id: targetVelocityEstimator.id,
        })) ?? [],
    }),
    getTargetVelocityEstimator: builder.query<TVEProfile, { uuid: string }>({
      query: ({ uuid }) => `/profiles/velocity/profiles/${uuid}`,
      transformResponse: (response: TVEProfile) =>
        TVEProfile.fromJSON(response),
      providesTags: (targetVelocityTestimator) =>
        targetVelocityTestimator
          ? [
              {
                type: Tag.TARGET_VELOCITY_ESTIMATOR,
                id: targetVelocityTestimator.id,
              },
            ]
          : [],
    }),
    setTargetVelocityEstimator: builder.mutation<
      TVEProfile,
      { serial?: string; targetVelocityEstimator: TVEProfile }
    >({
      query: ({ serial, targetVelocityEstimator }) => ({
        url: `/profiles/velocity/robots/${serial}`,
        method: Method.POST,
        body: TVEProfile.toJSON(targetVelocityEstimator),
      }),
      transformResponse: (response: TVEProfile) =>
        TVEProfile.fromJSON(response),
      onQueryStarted: async ({ serial }, { dispatch, queryFulfilled }) => {
        const { data: newTargetVelocityEstimator } = await queryFulfilled;
        dispatch(
          portalApi.util.updateQueryData(
            "listTargetVelocityEstimators",
            { serial },
            (targetVelocityEstimators) => {
              const index = targetVelocityEstimators.findIndex(
                (targetVelocityEstimator) =>
                  targetVelocityEstimator.id === newTargetVelocityEstimator.id
              );
              if (index === -1) {
                targetVelocityEstimators.push(newTargetVelocityEstimator);
              } else {
                targetVelocityEstimators[index] = newTargetVelocityEstimator;
              }
              return targetVelocityEstimators;
            }
          )
        );
        dispatch(
          portalApi.util.updateQueryData(
            "getTargetVelocityEstimator",
            { uuid: newTargetVelocityEstimator.id },
            () => newTargetVelocityEstimator
          )
        );
      },
    }),

    // PLANT PROFILE
    listCustomerPlantProfiles: builder.query<
      ListPlantProfilesResponse,
      ProfileListRequest
    >({
      query: (params) => ({
        url: `/profiles/plant-profiles/customer`,
        params,
      }),
      transformResponse: (response: ListPlantProfilesResponse) =>
        ListPlantProfilesResponse.fromJSON(response),
      providesTags: (ppResp) => [
        Tag.PLANT_PROFILE,
        ...(ppResp?.plantProfiles.map((pp) => ({
          type: Tag.PLANT_PROFILE,
          id: pp.profile?.id,
        })) ?? []),
      ],
    }),
    listCustomerPlantProfilesCursor: builder.infiniteQuery<
      ListPlantProfilesResponse,
      ProfileListRequest,
      string | undefined
    >({
      query: ({ queryArg, pageParam }) => ({
        url: `/profiles/plant-profiles/customer`,
        params: {
          ...queryArg,
          ...(pageParam ? { olderCursor: pageParam } : {}),
        },
      }),
      infiniteQueryOptions: {
        initialPageParam: undefined, // cursor based pagination
        getNextPageParam: (lastPage) => lastPage.pagination?.nextOld,
      },
      transformResponse: (response: ListPlantProfilesResponse) =>
        ListPlantProfilesResponse.fromJSON(response),
      providesTags: (response) => [
        Tag.PLANT_PROFILE,
        ...(response?.pages.flatMap((page) =>
          page.plantProfiles.map((pp) => ({
            type: Tag.PLANT_PROFILE,
            id: pp.profile?.id,
          }))
        ) ?? []),
      ],
    }),
    listGlobalPlantProfiles: builder.query<
      ListPlantProfilesResponse,
      ProfileListRequest
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: (params) => ({
        url: `/profiles/plant-profiles/global`,
        params,
      }),
      transformResponse: (response: ListPlantProfilesResponse) =>
        ListPlantProfilesResponse.fromJSON(response),
      providesTags: (ppResp) => [
        Tag.ADMIN_PLANT_PROFILE,
        ...(ppResp?.plantProfiles.map((pp) => ({
          type: Tag.ADMIN_PLANT_PROFILE,
          id: pp.profile?.id,
        })) ?? []),
      ],
    }),
    listCategories: builder.query<ListCategoriesResponse, ProfileListRequest>({
      query: (params) => ({
        url: `/profiles/categories`,
        params,
      }),
      transformResponse: (response: ListCategoriesResponse) =>
        ListCategoriesResponse.fromJSON(response),
      providesTags: (resp) => [
        Tag.CATEGORY,
        ...(resp?.categories.map((category) => ({
          type: Tag.CATEGORY,
          id: category.category?.id,
        })) ?? []),
      ],
    }),
    listCategoriesCursor: builder.infiniteQuery<
      ListCategoriesResponse,
      ProfileListRequest,
      string | undefined
    >({
      query: ({ queryArg, pageParam }) => ({
        url: `/profiles/categories`,
        params: {
          ...queryArg,
          ...(pageParam ? { olderCursor: pageParam } : {}),
        },
      }),
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.pagination?.nextOld,
      },
      transformResponse: (response: ListCategoriesResponse) =>
        ListCategoriesResponse.fromJSON(response),
      providesTags: (response) => [
        Tag.CATEGORY,
        ...(response?.pages.flatMap((page) =>
          page.categories.map((category) => ({
            type: Tag.CATEGORY,
            id: category.category?.id,
          }))
        ) ?? []),
      ],
    }),
    getCategory: builder.query<
      SavedCategory,
      { uuid: string; includeDeleted?: boolean }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid, includeDeleted }) => ({
        url: `/profiles/categories/${uuid}`,
        params: includeDeleted ? { includeDeleted } : undefined,
      }),
      transformResponse: (response: SavedCategory) =>
        SavedCategory.fromJSON(response),
      providesTags: (_result, _error, { uuid }) => [
        { type: Tag.CATEGORY, id: uuid },
      ],
    }),
    applyCategoryChanges: builder.mutation<
      SavedCategory,
      { uuid: string; change: CategoryChange; customerUuid?: string }
    >({
      query: ({ uuid, change, customerUuid }) => ({
        url: `/profiles/categories/${uuid}`,
        method: Method.POST,
        body: CategoryChange.toJSON(change),
        params: customerUuid ? { customerUuid } : undefined,
      }),
      transformResponse: (response: SavedCategory) =>
        SavedCategory.fromJSON(response),
      onQueryStarted: async ({ uuid }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            portalApi.util.updateQueryData("getCategory", { uuid }, () => data)
          );
        } catch (error) {
          if (isStalePlantProfileSaveError(error)) {
            dispatch(
              portalApi.util.invalidateTags([{ type: Tag.CATEGORY, id: uuid }])
            );
          }
        }
      },
      invalidatesTags: (_result, _error, { uuid }) => [
        { type: Tag.CATEGORY, id: uuid },
        { type: Tag.CATEGORY_VERSIONS, id: uuid },
        Tag.CATEGORY,
      ],
    }),
    listCategoryVersions: builder.query<
      ListCategoryHistoryResponse,
      {
        uuid: string;
        limit: number;
        olderCursor?: string;
      }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid, limit, olderCursor }) => ({
        url: `/profiles/categories/${uuid}/versions`,
        params: { limit, olderCursor },
      }),
      transformResponse: (response: ListCategoryHistoryResponse) =>
        ListCategoryHistoryResponse.fromJSON(response),
      providesTags: (_result, _error, { uuid }) => [
        {
          type: Tag.CATEGORY_VERSIONS,
          id: uuid,
        },
      ],
    }),
    getCategoryVersion: builder.query<
      SavedLibraryCategory,
      {
        uuid: string;
        cvUuid: string;
      }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid, cvUuid }) => ({
        url: `/profiles/categories/${uuid}/versions/${cvUuid}`,
      }),
      transformResponse: (response: SavedLibraryCategory) =>
        SavedLibraryCategory.fromJSON(response),
    }),
    listPlantProfileVersions: builder.query<
      ListCategoryCollectionHistoryResponse,
      {
        uuid: string;
        limit: number;
        olderCursor?: string;
      }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid, limit, olderCursor }) => ({
        url: `/profiles/plant-profiles/${uuid}/versions`,
        params: { limit, olderCursor },
      }),
      transformResponse: (response: ListCategoryCollectionHistoryResponse) =>
        ListCategoryCollectionHistoryResponse.fromJSON(response),
      providesTags: (result, error, { uuid }) => [
        {
          type: Tag.PLANT_PROFILE_VERSIONS,
          id: uuid,
        },
      ],
    }),
    getPlantProfileVersion: builder.query<
      PortalSavedPlantProfile,
      {
        ccUuid: string;
        ccvUuid: string;
      }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ ccUuid, ccvUuid }) => ({
        url: `/profiles/plant-profiles/${ccUuid}/versions/${ccvUuid}`,
      }),
      transformResponse: (response: PortalSavedPlantProfile) =>
        PortalSavedPlantProfile.fromJSON(response),
    }),
    getCustomerPlantProfile: builder.query<
      SavedPlantProfile,
      { uuid: string; customerUuid: string }
    >({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid, customerUuid }) => ({
        url: `/profiles/plant-profiles/${uuid}`,
        params: { customerUuid },
      }),
      transformResponse: (response: SavedPlantProfile) =>
        SavedPlantProfile.fromJSON(response),
      providesTags: (result, error, { uuid }) => [
        {
          type: Tag.PLANT_PROFILE,
          id: uuid,
        },
      ],
    }),
    getGlobalPlantProfile: builder.query<SavedPlantProfile, { uuid: string }>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: ({ uuid }) => `/profiles/plant-profiles/global/${uuid}`,
      transformResponse: (response: SavedPlantProfile) =>
        SavedPlantProfile.fromJSON(response),
      providesTags: (result, error, { uuid }) => [
        {
          type: Tag.ADMIN_PLANT_PROFILE,
          id: uuid,
        },
      ],
    }),
    applyGlobalPlantProfileChanges: builder.mutation<
      SavedPlantProfile,
      {
        ccUUID: string;
        changes: PlantProfileChanges;
      }
    >({
      query: ({ ccUUID, changes }) => {
        return {
          url: `/profiles/plant-profiles/global/${ccUUID}`,
          method: Method.POST,
          body: PlantProfileChanges.toJSON(changes),
        };
      },
      transformResponse: (response: SavedPlantProfile) =>
        SavedPlantProfile.fromJSON(response),
      onQueryStarted: async (
        { changes, ccUUID },
        { dispatch, queryFulfilled }
      ) => {
        try {
          const { data: newPlantProfile } = await queryFulfilled;
          if (
            (changes.collection?.create ||
              changes.collection?.update ||
              changes.categories.some((c) => c.create || c.update)) &&
            newPlantProfile.profile?.id
          ) {
            dispatch(
              portalApi.util.updateQueryData(
                "getGlobalPlantProfile",
                {
                  uuid: newPlantProfile.profile.id,
                },
                () => newPlantProfile
              )
            );
          }
        } catch (error) {
          if (isStalePlantProfileSaveError(error)) {
            dispatch(
              portalApi.util.invalidateTags([
                { type: Tag.ADMIN_PLANT_PROFILE, id: ccUUID },
              ])
            );
          }
        }
      },
      invalidatesTags: (result, error, { ccUUID, changes }) => {
        const invalidate = [
          {
            type: Tag.PLANT_PROFILE_VERSIONS,
            id: ccUUID,
          },
          Tag.ADMIN_PLANT_PROFILE,
        ];
        return changes.collection?.delete
          ? [
              ...invalidate,
              {
                type: Tag.ADMIN_PLANT_PROFILE,
                id: changes.collection.delete.id,
              },
            ]
          : invalidate;
      },
    }),
    applyCustomerPlantProfileChanges: builder.mutation<
      SavedPlantProfile,
      {
        customerUuid?: string;
        ccUUID: string;
        changes: PlantProfileChanges;
      }
    >({
      query: ({ customerUuid, ccUUID, changes }) => ({
        url: `/profiles/plant-profiles/${ccUUID}`,
        method: Method.POST,
        body: PlantProfileChanges.toJSON(changes),
        params: customerUuid ? { customerUuid } : undefined,
      }),
      transformResponse: (response: SavedPlantProfile) =>
        SavedPlantProfile.fromJSON(response),
      onQueryStarted: async (
        { changes, customerUuid, ccUUID },
        { dispatch, queryFulfilled }
      ) => {
        try {
          const { data: newPlantProfile } = await queryFulfilled;
          if (
            (changes.collection?.create ||
              changes.collection?.update ||
              changes.categories.some((c) => c.create || c.update)) &&
            newPlantProfile.profile?.id &&
            customerUuid
          ) {
            dispatch(
              portalApi.util.updateQueryData(
                "getCustomerPlantProfile",
                {
                  uuid: newPlantProfile.profile.id,
                  customerUuid,
                },
                () => newPlantProfile
              )
            );
          }
        } catch (error) {
          if (isStalePlantProfileSaveError(error)) {
            dispatch(
              portalApi.util.invalidateTags([
                { type: Tag.PLANT_PROFILE, id: ccUUID },
              ])
            );
          }
        }
      },
      invalidatesTags: (result, error, { ccUUID, changes }) => {
        const invalidate = [
          {
            type: Tag.PLANT_PROFILE_VERSIONS,
            id: ccUUID,
          },
        ];
        return changes.collection?.delete
          ? [
              ...invalidate,
              {
                type: Tag.PLANT_PROFILE,
                id: changes.collection.delete.id,
              },
            ]
          : invalidate;
      },
    }),
    getGlobals: builder.query<Globals, void>({
      keepUnusedDataFor: 60 * 60, // 1 hour
      query: () => "/globals",
      transformResponse: (response: any) => Globals.fromJSON(response),
      providesTags: [Tag.GLOBAL],
    }),
    setGlobals: builder.mutation<Globals, UpdateGlobalsRequest>({
      query: (global) => {
        return {
          url: `/globals`,
          method: Method.PUT,
          body: UpdateGlobalsRequest.toJSON(global),
        };
      },
      transformResponse: (response: Globals) => Globals.fromJSON(response),
      invalidatesTags: [Tag.GLOBAL],
    }),
  }),
  overrideExisting: "throw",
});

export const portalEndpoints = portalApi.endpoints;
